import { agentDisplayName, agentDisplayTitle, agentDisplayReport } from './presentation';
import { useEffect, useId, useRef, useState } from "react";
import { API_BASE, getAuthHeaders } from "../../utils/supabase/client";
import { Button } from "../../shared/ui/button";
import { AIResponseMarkdown } from "../../shared/ui/ai-response-markdown";
import { useSI } from "../../contexts/SIContext";
import { openTakeoffLinkedSource } from "../takeoff/utils/linkedSource";

type Job = {
  runId: string;
  projectId: string;
  role: string;
  task: string;
  status: "queued" | "processing" | "waiting" | "completed" | "failed";
};
type Detail = Job & {
  progress?: {
    processedLines?: number;
    pricedLines?: number;
    unpricedLines?: number;
    message?: string;
  };
  result?: {
    text?: string;
    draftIds?: string[];
    processedLines?: number;
    pricedLines?: number;
    unpricedLines?: number;
    /** quantisai_takeoff: where the suggestions were saved and how many await review. */
    sheetId?: string;
    page?: number;
    drawing?: string;
    pendingSuggestions?: number;
  };
  error?: string;
};
export function SIAgentTasks(
  { workspaceId, conversationId, refresh, onReviewDraft, onReviewRun }: {
    workspaceId: string | null;
    /** The chat whose tasks are shown; tasks belong to the chat that started them. */
    conversationId?: string | null;
    refresh: number;
    onReviewDraft?: (draftId: string) => void;
    onReviewRun?: (runId: string) => void;
  },
) {
  const { close: closeSI } = useSI();
  const [jobs, setJobs] = useState<Job[]>([]);
  const jobsRef = useRef<Job[]>([]);
  const [details, setDetails] = useState<Record<string, Detail>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyId = useId();
  const scopeKey = `${workspaceId ?? ""}|${conversationId ?? ""}`;
  const [viewScope, setViewScope] = useState(scopeKey);
  const workspaceRef = useRef(workspaceId);
  workspaceRef.current = workspaceId;
  const inspection = useRef<AbortController | null>(null);
  useEffect(() => {
    inspection.current?.abort();
    inspection.current = null;
    setBusy(null);
    setHistoryOpen(false);
    return () => {
      inspection.current?.abort();
      inspection.current = null;
    };
  }, [scopeKey]);
  // A confirmed proposal queues a job without a new chat message, so the
  // card that confirmed it asks for a reload instead.
  useEffect(() => {
    const reload = () => setRevision((value) => value + 1);
    window.addEventListener("si:agent-tasks-refresh", reload);
    return () => window.removeEventListener("si:agent-tasks-refresh", reload);
  }, []);
  useEffect(() => {
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    setViewScope(scopeKey);
    jobsRef.current = [];
    setJobs([]);
    setDetails({});
    setError(null);
    const load = async () => {
      try {
        const query = new URLSearchParams();
        if (workspaceId) query.set("workspaceId", workspaceId);
        if (conversationId) query.set("conversationId", conversationId);
        const response = await fetch(
          `${API_BASE}/si/agent-tasks${query.size ? `?${query}` : ""}`,
          { headers: await getAuthHeaders(), signal: abort.signal },
        );
        if (!response.ok) throw new Error("Agent tasks could not be checked.");
        const value = await response.json() as { tasks: Job[] };
        if (abort.signal.aborted) return;
        if (!Array.isArray(value.tasks)) {
          throw new Error("Agent tasks could not be checked.");
        }
        // A takeoff that finished while this was polling has just saved
        // suggestions; an open Takeoff sheet re-reads them.
        const previous = jobsRef.current;
        if (value.tasks.some((job) =>
          job.role === "quantisai_takeoff" && job.status === "completed" &&
          previous.some((before) => before.runId === job.runId && before.status !== "completed")
        )) window.dispatchEvent(new Event("takeoff:refresh-suggestions"));
        jobsRef.current = value.tasks;
        setJobs(value.tasks);
        if (
          value.tasks.some((job) =>
            ["queued", "processing", "waiting"].includes(job.status)
          )
        ) timer = setTimeout(() => void load(), 5000);
      } catch (cause) {
        if (!abort.signal.aborted) {
          setError(
            cause instanceof Error ? cause.message : "Agent tasks unavailable.",
          );
        }
      }
    };
    void load();
    return () => {
      abort.abort();
      if (timer) clearTimeout(timer);
    };
  }, [workspaceId, conversationId, refresh, revision]);
  const inspect = async (job: Job, stop = false) => {
    if (busy) return;
    const scope = workspaceId;
    const abort = new AbortController();
    inspection.current = abort;
    setBusy(job.runId);
    setError(null);
    try {
      const path = `${API_BASE}/si/agent-tasks/${
        encodeURIComponent(job.runId)
      }`;
      const response = await fetch(
        stop
          ? `${path}/cancel`
          : `${path}${
            scope ? `?workspaceId=${encodeURIComponent(scope)}` : ""
          }`,
        {
          method: stop ? "POST" : "GET",
          headers: {
            ...await getAuthHeaders(),
            ...(stop ? { "Content-Type": "application/json" } : {}),
          },
          ...(stop ? { body: JSON.stringify({ workspaceId }) } : {}),
          signal: abort.signal,
        },
      );
      if (!response.ok) throw new Error("Agent task is no longer available.");
      const detail = await response.json() as Detail;
      if (
        abort.signal.aborted || workspaceRef.current !== scope ||
        inspection.current !== abort
      ) return;
      if (stop) setRevision((value) => value + 1);
      else setDetails((value) => ({ ...value, [job.runId]: detail }));
    } catch (cause) {
      if (
        !abort.signal.aborted && workspaceRef.current === scope &&
        inspection.current === abort
      ) {
        setError(
          cause instanceof Error ? cause.message : "Agent task unavailable.",
        );
      }
    } finally {
      if (inspection.current === abort) {
        inspection.current = null;
        setBusy(null);
      }
    }
  };
  if (viewScope !== scopeKey) return null;
  if (!jobs.length) {
    return error
      ? <p role="status" className="text-xs text-muted-foreground">{error}</p>
      : null;
  }
  const reviewTakeoff = (job: Job, result: NonNullable<Detail["result"]>) => {
    openTakeoffLinkedSource({
      projectId: job.projectId,
      sheetId: typeof result.sheetId === "string" && result.sheetId
        ? result.sheetId
        : undefined,
      page: result.page,
      review: true,
    });
    // The review tray lives in the inspector this panel occupies, and on a
    // phone this panel covers the drawing.
    closeSI();
  };
  const renderJob = (job: Job) => {
    const detail = details[job.runId];
    const running = ["queued", "processing", "waiting"].includes(
      job.status,
    );
    return (
      <div key={job.runId} className="rounded-lg border p-3 text-sm">
        <p className="font-medium">{agentDisplayName(job.runId)}</p>
        {running && <p className="text-xs text-muted-foreground">Working in the cloud. You can leave this chat.</p>}
        <p className="text-xs text-muted-foreground">{agentDisplayTitle(job)}</p>
        <p className="mt-1 text-xs text-muted-foreground" role="status">
          {job.status === "waiting"
            ? "Continuing with specialists or the next batch"
            : job.status === "processing"
            ? "Working"
            : job.status === "queued"
            ? "Queued"
            : job.status === "completed"
            ? "Ready to review"
            : "Stopped or failed"}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy !== null}
          onClick={() => void inspect(job)}
        >
          View {running ? "progress" : "result"}
        </Button>
        {running && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy !== null}
            onClick={() => void inspect(job, true)}
          >
            Stop
          </Button>
        )}
        {detail?.progress?.processedLines !== undefined && (
          <p className="text-xs">
            {detail.progress.processedLines} lines reviewed ·{" "}
            {detail.progress.pricedLines ?? 0} matched ·{" "}
            {detail.progress.unpricedLines ?? 0} unresolved
          </p>
        )}
        {job.status !== "completed" && detail?.progress?.message && (
          <p className="text-xs">{detail.progress.message}</p>
        )}
        {detail?.result?.text && (
          <AIResponseMarkdown text={agentDisplayReport(detail.result.text)} />
        )}
        {job.role === "quantisai_takeoff" && job.status === "completed" &&
          detail?.result && (detail.result.pendingSuggestions ?? 0) > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => reviewTakeoff(job, detail.result!)}
          >
            Review {detail.result.pendingSuggestions}{" "}
            {detail.result.pendingSuggestions === 1 ? "suggestion" : "suggestions"}
          </Button>
        )}
        {detail?.result?.draftIds && (
          <>
            <p className="mt-2">
              {detail.result.processedLines} lines reviewed;{" "}
              {detail.result.pricedLines} proposed prices and{" "}
              {detail.result.unpricedLines}{" "}
              unresolved. Prices await review and confirmation.
            </p>
            {/* A run spreads its prices over many small drafts; offer them as one review. */}
            {onReviewRun && detail.result.draftIds.length > 1
              ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  disabled={!detail.result.pricedLines}
                  onClick={() => onReviewRun(job.runId)}
                >
                  Review and apply {detail.result.pricedLines ?? 0} priced lines
                </Button>
              )
              : detail.result.draftIds.map((id, index) => (
              <Button
                key={id}
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 mr-2"
                disabled={!onReviewDraft}
                aria-label={`Review pricing draft ${index + 1}`}
                onClick={() => onReviewDraft?.(id)}
              >
                Review {detail.result!.draftIds!.length > 1
                  ? `draft ${index + 1}`
                  : "pricing draft"}
              </Button>
            ))}
          </>
        )}
        {detail?.error && (
          <p role="alert" className="mt-2 text-destructive">
            This task needs attention. Review the available results or try again.
          </p>
        )}
      </div>
    );
  };
  const activeJobs = jobs.filter((job) =>
    ["queued", "processing", "waiting"].includes(job.status)
  );
  const pastJobs = jobs.filter((job) =>
    !["queued", "processing", "waiting"].includes(job.status)
  );
  return (
    <section aria-label="Agent tasks" className="mt-3 space-y-2">
      {activeJobs.length > 0 && (
        <p className="text-xs font-medium text-muted-foreground">Agent tasks</p>
      )}
      {activeJobs.map(renderJob)}
      {pastJobs.length > 0 && (
        <>
          <button
            type="button"
            className="inline-flex items-center gap-1 py-1 text-xs text-muted-foreground hover:text-foreground"
            aria-label="Agent tasks history"
            aria-expanded={historyOpen}
            aria-controls={historyId}
            onClick={() => setHistoryOpen((value) => !value)}
          >
            <span
              aria-hidden="true"
              className={historyOpen
                ? "rotate-90 transition-transform"
                : "transition-transform"}
            >
              ›
            </span>
            {activeJobs.length ? "Past agent tasks" : "Agent tasks"}
            <span className="text-muted-foreground">({pastJobs.length})</span>
          </button>
          {historyOpen && (
            <div id={historyId} className="space-y-2">
              {pastJobs.map(renderJob)}
            </div>
          )}
        </>
      )}
      {error && <p role="alert" className="text-xs text-destructive">{error}
      </p>}
    </section>
  );
}
